/**
 * @file pages/well/SummarizeReportPage.js
 * @description Well & Septic Butler — "Summarize a report".
 *
 * James's ask (2026-09-24): plug in any inspection report as a PDF, get one
 * plain-English page in front of it, and the report you uploaded starting on
 * page 2 — all one document.
 *
 * Phone-first, same shell and primitives as every other well screen (WellPage,
 * Card, Button, ErrorNote). Drag a file on, or tap to pick one. While it works
 * the screen says what it is doing in words, not a spinner. When it is done you
 * get the file to download, a preview of it right here, and a way to start over.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card } from '../../design-system';
import { WellPage, ErrorNote } from './wellShared';
import { API } from '../../lib/api';

const MAX_MB = 40;

/** The words the screen shows while it works, in order. */
const STAGES = [
  { key: 'uploading', label: 'Uploading your report' },
  { key: 'reading', label: 'Reading it' },
  { key: 'writing', label: 'Writing the summary' },
  { key: 'building', label: 'Building your PDF' },
];

/**
 * The length dial (James, 2026-09-25). Short is what shipped 9/24 and stays
 * the default. Medium and Long add a plain-words write-up under the checklist.
 * Nothing is stored on the server, so changing the length re-reads the same
 * file — the one still on this screen, or a finished file uploaded again
 * (the server takes the old summary off the front first).
 */
const LENGTHS = [
  { key: 'short', label: 'Short', hint: 'A few lines' },
  { key: 'medium', label: 'Medium', hint: 'About half a page' },
  { key: 'long', label: 'Long', hint: 'About a full page' },
];

export default function SummarizeReportPage() {
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState(null);      // null | one of STAGES[].key
  const [pct, setPct] = useState(0);             // upload percent
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [length, setLength] = useState('short');
  const inputRef = useRef(null);
  const xhrRef = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    if (xhrRef.current) xhrRef.current.abort();
  }, []);

  const pick = useCallback((f) => {
    setError(null);
    setResult(null);
    if (!f) return;
    const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');
    if (!isPdf) {
      setError({ message: 'That file is not a PDF. This tool reads PDFs only — if you have a photo or a Word file, save it as a PDF first.' });
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError({ message: `That file is bigger than ${MAX_MB} MB. Split the report, or save it smaller, and try again.` });
      return;
    }
    setFile(f);
  }, []);

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    pick(f);
  }

  function startOver() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (xhrRef.current) { xhrRef.current.abort(); xhrRef.current = null; }
    setFile(null); setStage(null); setPct(0); setResult(null); setError(null); setLength('short');
    if (inputRef.current) inputRef.current.value = '';
  }

  function run(len = length) {
    if (!file) return;
    setError(null); setResult(null); setPct(0); setStage('uploading');

    const form = new FormData();
    form.append('length', len);
    form.append('file', file, file.name);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.open('POST', `${API}/v1/well/summarize`);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) setPct(Math.round((evt.loaded / evt.total) * 100));
    };
    xhr.upload.onload = () => {
      // The bytes are there; the server now reads, writes and builds. We cannot
      // see inside one request, so the words move on a timer that matches how
      // long those steps actually take.
      setStage('reading');
      timersRef.current.push(setTimeout(() => setStage('writing'), 4000));
      timersRef.current.push(setTimeout(() => setStage('building'), 16000));
    };
    xhr.onerror = () => {
      setStage(null);
      setError({ message: 'The connection dropped before I could finish. Try again.' });
    };
    xhr.onload = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      setStage(null);
      let json = null;
      try { json = JSON.parse(xhr.responseText); } catch (e) { /* no body */ }
      if (xhr.status === 200 && json && json.success) {
        setResult(json.data);
      } else {
        setError({ message: (json && json.error && json.error.message)
          || `Something went wrong (${xhr.status}). Nothing was saved. Try again in a minute.` });
      }
    };
    xhr.send(form);
  }

  const working = stage !== null;
  const stageIndex = STAGES.findIndex((s) => s.key === stage);

  return (
    <WellPage
      title="Summarize a report"
      intro="Upload any inspection report PDF — well, septic, sewer, water test or whole house. You get a plain-English summary in front of it — short, medium or long — and your report right behind it, all in one file. Already have a file this tool made? Upload it again to get a new summary at a different length."
    >
      {error && <ErrorNote error={error} />}

      {!result && !working && (
        <Card variant="elevated" padding="md">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => pick(e.target.files && e.target.files[0])}
            style={{ display: 'none' }}
          />

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current && inputRef.current.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current.click(); } }}
            role="button"
            tabIndex={0}
            aria-label="Choose a PDF, or drop one here"
            style={{
              minHeight: 170,
              borderRadius: 'var(--radius-lg)',
              border: `2px dashed ${dragging ? 'var(--color-primary)' : 'var(--border-default)'}`,
              background: dragging ? 'var(--surface-sunken)' : 'transparent',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', padding: 'var(--space-4)', cursor: 'pointer',
              marginBottom: 'var(--space-3)',
            }}
          >
            {file ? (
              <>
                <div style={{ fontWeight: 'var(--weight-bold)', wordBreak: 'break-word' }}>{file.name}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginTop: 4 }}>
                  {(file.size / (1024 * 1024)).toFixed(1)} MB · tap to pick a different one
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 'var(--weight-bold)' }}>Drop a PDF here, or tap to pick one</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginTop: 4 }}>
                  Up to {MAX_MB} MB, up to 60 pages
                </div>
              </>
            )}
          </div>

          <LengthDial value={length} onChange={setLength} label="How long should the summary be?" />

          <Button fullWidth size="lg" disabled={!file} onClick={() => run(length)}>
            Summarize this report
          </Button>
        </Card>
      )}

      {working && (
        <Card variant="elevated" padding="md" aria-live="polite">
          <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-3)' }}>
            Working on it
          </div>
          {STAGES.map((s, i) => {
            const done = i < stageIndex;
            const now = i === stageIndex;
            return (
              <div key={s.key} style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                padding: 'var(--space-2) 0',
                color: now ? 'var(--text-primary)' : done ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                fontWeight: now ? 'var(--weight-bold)' : 'var(--weight-regular)',
              }}>
                <span aria-hidden="true" style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${now || done ? 'var(--color-primary)' : 'var(--border-default)'}`,
                  background: done ? 'var(--color-primary)' : 'transparent',
                }} />
                <span>{s.label}{now && s.key === 'uploading' ? ` — ${pct}%` : ''}</span>
              </div>
            );
          })}
          <p style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', lineHeight: 1.5 }}>
            A long report can take a minute. Keep this page open.
          </p>
          <Button fullWidth variant="secondary" onClick={startOver} style={{ marginTop: 'var(--space-3)' }}>
            Cancel
          </Button>
        </Card>
      )}

      {result && (
        <Result
          data={result}
          length={length}
          onLength={(k) => { if (k !== length) { setLength(k); run(k); } }}
          onStartOver={startOver}
        />
      )}

      <p style={{ marginTop: 'var(--space-5)', color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', lineHeight: 1.5 }}>
        Your report is not kept. The finished file is deleted 24 hours after it is made, and the copy you
        uploaded only ever exists inside it. The summary is written by AI from what the report says — it can
        miss things, so the report itself stays the record.
      </p>
    </WellPage>
  );
}

/** Short / Medium / Long — same look and keyboard as the Pass/Fail/NA picker. */
function LengthDial({ value, onChange, label, disabled = false }) {
  function onKey(e, i) {
    let j = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % LENGTHS.length;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + LENGTHS.length) % LENGTHS.length;
    if (j === null) return;
    e.preventDefault();
    const btn = e.currentTarget.parentElement.children[j];
    if (btn) btn.focus();
  }
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div id="summ-length-label" style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-2)' }}>
        {label}
      </div>
      <div
        role="radiogroup"
        aria-labelledby="summ-length-label"
        style={{ display: 'grid', gridTemplateColumns: `repeat(${LENGTHS.length}, 1fr)`, gap: 'var(--space-2)' }}
      >
        {LENGTHS.map((o, i) => {
          const on = value === o.key;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(o.key)}
              onKeyDown={(e) => onKey(e, i)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: 56, padding: 'var(--space-2)',
                border: `2px solid ${on ? 'var(--color-primary)' : 'var(--border-default)'}`,
                borderRadius: 'var(--radius-lg)',
                background: on ? 'var(--color-primary-subtle)' : 'var(--surface-raised)',
                color: on ? 'var(--color-primary)' : 'var(--text-secondary)',
                fontFamily: 'var(--font-family)', cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1, WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span style={{ fontWeight: 'var(--weight-bold)' }}>{o.label}</span>
              <span style={{ fontSize: 'var(--text-caption)', color: on ? 'var(--color-primary)' : 'var(--text-tertiary)', marginTop: 2 }}>
                {o.hint}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Result({ data, length, onLength, onStartOver }) {
  const url = data.downloadUrl;
  const pages = data.pages || {};
  const meta = data.meta || {};
  const verdict = data.verdict || null;
  const jobs = (data.checklist || []).length;

  return (
    <div>
      <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--text-heading-md, 1.3rem)', fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-1)' }}>
          Your document is ready
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginBottom: 'var(--space-3)' }}>
          {pages.summary} summary page{pages.summary === 1 ? '' : 's'}, then your {pages.original} page
          {pages.original === 1 ? '' : 's'} — your report starts on page {(pages.summary || 1) + 1} and is unchanged.
        </div>

        {meta.offline && (
          <div style={{
            border: '1px solid var(--border-default)', borderLeft: '4px solid var(--color-primary)',
            borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-3)',
            marginBottom: 'var(--space-3)', fontSize: 'var(--text-body-sm)', lineHeight: 1.5,
          }}>
            <strong>No summary was written.</strong> The reading brain is not switched on for this server, so
            page one says so and makes no permit call. Your report is still stapled behind it, untouched.
          </div>
        )}

        {!meta.offline && verdict && (
          <div style={{
            border: `2px solid ${verdict.kind === 'permit' || verdict.kind === 'ask' ? 'var(--color-primary)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', marginBottom: 'var(--space-3)',
          }}>
            <div style={{
              fontSize: '1.2rem', fontWeight: 'var(--weight-bold)',
              color: verdict.kind === 'permit' || verdict.kind === 'ask' ? 'var(--color-primary)' : 'var(--text-primary)',
            }}>
              {verdict.headline}
            </div>
            {verdict.detail && (
              <div style={{ marginTop: 4, fontSize: 'var(--text-body-sm)', lineHeight: 1.5 }}>{verdict.detail}</div>
            )}
            <div style={{ marginTop: 6, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>
              {jobs} job{jobs === 1 ? '' : 's'} on the list · the permit call comes from the office permit table, not the AI
            </div>
          </div>
        )}

        {meta.replacedOldSummary && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginBottom: 'var(--space-3)', lineHeight: 1.5 }}>
            This file already had a summary on the front. I took the old one off and put the new one in its place.
          </div>
        )}

        <LengthDial value={length} onChange={onLength} label="Want it shorter or longer? Tap one to make it again." />

        <a href={url} download="report-with-summary.pdf" style={{ textDecoration: 'none', display: 'block' }}>
          <Button fullWidth size="lg">Download the document</Button>
        </a>
        <Button fullWidth variant="secondary" onClick={onStartOver} style={{ marginTop: 'var(--space-3)' }}>
          Start over
        </Button>
      </Card>

      <Card variant="outline" padding="sm">
        <div style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-2)' }}>Preview</div>
        <object
          data={url}
          type="application/pdf"
          aria-label="Preview of the finished document"
          style={{ width: '100%', height: '70vh', minHeight: 380, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}
        >
          <p style={{ margin: 0, fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
            Your browser will not show the PDF here. Use the download button above.
          </p>
        </object>
      </Card>
    </div>
  );
}
